import { PDFDocument } from 'pdf-lib';

/**
 * Splits a multi-page PDF into separate single-page PDFs
 * @param {File} pdfFile - The original PDF file
 * @returns {Promise<Array<File>>} Array of single-page PDF files
 */
export const splitPDF = async (pdfFile) => {
    try {
        const arrayBuffer = await pdfFile.arrayBuffer();

        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const pageCount = pdfDoc.getPageCount();

        if (pageCount <= 1) {
            return [pdfFile];
        }

        const splitFiles = [];

        for (let i = 0; i < pageCount; i++) {
            const newPdfDoc = await PDFDocument.create();

            const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [i]);
            newPdfDoc.addPage(copiedPage);

            const pdfBytes = await newPdfDoc.save();

            const fileName = pdfFile.name.replace('.pdf', '');
            const newFileName = `${fileName}_page_${i + 1}.pdf`;
            const newFile = new File([pdfBytes], newFileName, {
                type: 'application/pdf',
                lastModified: Date.now()
            });

            splitFiles.push(newFile);
        }

        return splitFiles;
    } catch (error) {
        console.error('Error splitting PDF:', error);
        throw new Error('Failed to split PDF file');
    }
};

/**
 * Gets the number of pages in a PDF file
 * @param {File} pdfFile - The PDF file
 * @returns {Promise<number>} Number of pages
 */
export const getPDFPageCount = async (pdfFile) => {
    try {
        const arrayBuffer = await pdfFile.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        return pdfDoc.getPageCount();
    } catch (error) {
        console.error('Error getting PDF page count:', error);
        return 1;
    }
};

/**
 * Processes a PDF file and splits it if it has multiple pages
 * @param {File} pdfFile - The PDF file to process
 * @returns {Promise<Array<File>>} Array of processed PDF files
 */
export const processPDF = async (pdfFile) => {
    const pageCount = await getPDFPageCount(pdfFile);

    if (pageCount > 1) {
        return await splitPDF(pdfFile);
    } else {
        return [pdfFile];
    }
};
